import { useMemo } from "react";
import { Node } from "reactflow";

/**
 * A generic node processor hook that can:
 * - Update any node fields
 * - Apply updates to all, specific, or a single node by ID
 * - Optionally handle highlighting for a selected node
 *
 * @param nodes - The list of nodes
 * @param options - Optional configuration:
 *   - selectedNodeId?: string | null
 *   - update?: Partial<Node> | ((node: Node) => Node)
 *   - targetNodeIds?: string | string[]
 */
const useUpdatedLocalNodeValues = (
  nodes: Node[],
  options?: {
    selectedNodeId?: string | null;
    update?: Partial<Node> | ((node: Node) => Node);
    targetNodeIds?: string | string[];
  }
): Node[] => {
  const { selectedNodeId, update, targetNodeIds } = options || {};

  return useMemo(() => {
    // Normalize target IDs
    const targetIds = Array.isArray(targetNodeIds)
      ? targetNodeIds
      : targetNodeIds
      ? [targetNodeIds]
      : null;

    return nodes.map((node) => {
      let newNode = { ...node, type: node.type || "standardNode" };

      // Optional selected node logic
      if (selectedNodeId !== undefined) {
        newNode = {
          ...newNode,
          data: {
            ...newNode.data,
            isSelected: selectedNodeId === node.id,
          },
        };
      }

      // Apply update if provided
      if (update) {
        const shouldUpdate =
          !targetIds || targetIds.includes(node.id); // update all if no target IDs

        if (shouldUpdate) {
          if (typeof update === "function") {
            const updated = update(newNode);
            newNode = {
              ...updated,
              type: updated.type || "standardNode",
            };
          } else {
            const partial = update as Partial<Node>;
            newNode = {
              ...newNode,
              ...partial,
              type: partial.type || (newNode.type as string) || "standardNode",
              data: {
                ...newNode.data,
                ...partial.data,
                functionalities: node.data.functionalities,
              },
            };
          }
        }
      }

      return newNode;
    });
  }, [nodes, selectedNodeId, update, targetNodeIds]);
};

export default useUpdatedLocalNodeValues;
